import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Mail01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Select } from "@/components/base/select/select";
import { LanguageSelector } from "@/components/application/language-selector/language-selector";
import { useCreateContactInquiry, type ContactInquiryRequestType } from "@/hooks/api/use-contact";
import { useMe, getRedirectPath } from "@/hooks/api/use-auth";
import { useToast } from "@/hooks/use-toast";

type ContactFormState = {
    fullName: string;
    workEmail: string;
    company: string;
    requestType: ContactInquiryRequestType;
    relationshipType?: string;
    message: string;
    privacyConsent: boolean;
};

type ContactFormErrors = Partial<Record<keyof ContactFormState, string>>;

const REQUEST_TYPE_ITEMS: Array<{ id: ContactInquiryRequestType; labelKey: string }> = [
    { id: "demo", labelKey: "fields.requestType.options.demo" },
    { id: "general", labelKey: "fields.requestType.options.general" },
];

export const ContactPage = () => {
    const { t } = useTranslation(["pages:contact", "pages:landing"]);
    const toast = useToast();
    const createInquiry = useCreateContactInquiry();
    const { data: currentUser } = useMe();

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";
    const fixedTopBar =
        hasEnvironmentBanner && (env === "development" || env === "staging" || import.meta.env.DEV);

    const chapters = useMemo(
        () => [
            { id: "problem", label: t("chapters.problem", { ns: "pages:landing" }) },
            { id: "solution", label: t("chapters.solution", { ns: "pages:landing" }) },
            { id: "features", label: t("chapters.features", { ns: "pages:landing" }) },
            { id: "world", label: t("chapters.world", { ns: "pages:landing" }) },
            { id: "pricing", label: t("chapters.pricing", { ns: "pages:landing" }) },
            { id: "cta", label: t("chapters.cta", { ns: "pages:landing" }) },
        ],
        [t],
    );

    const [form, setForm] = useState<ContactFormState>({
        fullName: "",
        workEmail: "",
        company: "",
        requestType: "demo",
        relationshipType: undefined,
        message: "",
        privacyConsent: false,
    });
    const [errors, setErrors] = useState<ContactFormErrors>({});
    const [submitted, setSubmitted] = useState(false);

    const requestTypeItems = useMemo(
        () => REQUEST_TYPE_ITEMS.map((item) => ({ id: item.id, label: t(item.labelKey) })),
        [t],
    );
    const relationshipTypeItems = useMemo(
        () =>
            [
                "shipper",
                "receiver",
                "notify_party",
                "freight_forwarder",
                "carrier",
                "customs_broker",
                "port_authority",
                "insurance_provider",
                "inspection_agency",
                "regulatory_authority",
                "land_transport",
                "supplier",
                "customer",
                "other",
            ].map((value) => ({
                id: value,
                label: t(`fields.relationshipType.options.${value}`),
            })),
        [t],
    );

    const validate = (): ContactFormErrors => {
        const nextErrors: ContactFormErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!form.fullName.trim()) nextErrors.fullName = t("validation.fullNameRequired");
        if (!form.workEmail.trim()) nextErrors.workEmail = t("validation.workEmailRequired");
        else if (!emailRegex.test(form.workEmail.trim())) nextErrors.workEmail = t("validation.workEmailInvalid");
        if (!form.company.trim()) nextErrors.company = t("validation.companyRequired");
        if (!form.message.trim()) nextErrors.message = t("validation.messageRequired");
        else if (form.message.trim().length > 2000) nextErrors.message = t("validation.messageTooLong");
        if (!form.privacyConsent) nextErrors.privacyConsent = t("validation.privacyConsentRequired");

        return nextErrors;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextErrors = validate();
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        try {
            await createInquiry.mutateAsync({
                full_name: form.fullName.trim(),
                work_email: form.workEmail.trim().toLowerCase(),
                company: form.company.trim(),
                request_type: form.requestType,
                relationship_type: form.relationshipType,
                message: form.message.trim(),
                privacy_consent: form.privacyConsent,
                source_page: "landing_contact_page",
            });
            setSubmitted(true);
            toast.success(t("submit.successToast"));
        } catch (error) {
            toast.error(error);
        }
    };

    return (
        <div className={`relative min-h-screen bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute -right-32 -top-24 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl motion-safe:animate-blob" />
                <div className="absolute -left-24 top-2/5 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl motion-safe:animate-blob motion-safe:animation-delay-2000" />
                <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full bg-purple-500/20 blur-3xl motion-safe:animate-blob motion-safe:animation-delay-4000" />
            </div>
            <header
                className={`z-30 border-b border-secondary_alt bg-primary/80 backdrop-blur-sm ${
                    fixedTopBar ? "fixed left-0 right-0 top-7" : "sticky top-0"
                }`}
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between gap-2 sm:gap-4">
                        <Link to="/" className="min-w-0 shrink">
                            <img
                                src="/images/logo-full.svg"
                                alt="EveMind"
                                className="h-8 w-auto max-w-[min(11rem,46vw)] sm:h-9 sm:max-w-none"
                            />
                        </Link>
                        <nav className="hidden lg:flex items-center gap-5">
                            {chapters.map((chapter) => (
                                <Link
                                    key={chapter.id}
                                    to={`/#${chapter.id}`}
                                    className="text-sm text-tertiary transition-colors hover:text-primary"
                                >
                                    {chapter.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
                            <Button
                                href="/contact"
                                size="sm"
                                color="secondary"
                                iconLeading={Mail01}
                                className="sm:hidden"
                                aria-label={t("hero.ctaContact", { ns: "pages:landing" })}
                            />
                            <Button href="/contact" size="sm" color="secondary" className="hidden sm:inline-flex">
                                {t("hero.ctaContact", { ns: "pages:landing" })}
                            </Button>
                            {currentUser ? (
                                <Button href={getRedirectPath(currentUser) ?? "/auth/login"} size="sm" color="primary">
                                    {t("hero.ctaGoToApp", { ns: "pages:landing" })}
                                </Button>
                            ) : (
                                <Button href="/auth/login" size="sm" color="primary">
                                    {t("hero.ctaLogin", { ns: "pages:landing" })}
                                </Button>
                            )}
                            <LanguageSelector showLabel={false} />
                        </div>
                    </div>
                </div>
            </header>

            {fixedTopBar ? <div className="h-16 shrink-0" aria-hidden /> : null}

            <main className="relative z-10 mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
                <section className="mx-auto max-w-2xl text-center">
                    <p className="text-sm font-semibold uppercase tracking-wider text-brand-600">{t("eyebrow")}</p>
                    <h1 className="mt-3 text-3xl font-semibold text-primary sm:text-4xl">{t("title")}</h1>
                    <p className="mt-4 text-base text-quaternary">{t("description")}</p>
                </section>
                <section className="mx-auto mt-8 w-full max-w-2xl rounded-2xl border border-secondary_alt bg-primary p-6 shadow-xl shadow-black/20 sm:p-8">
                        {submitted ? (
                            <div className="space-y-4">
                                <h2 className="text-2xl font-semibold text-primary">{t("submit.successTitle")}</h2>
                                <p className="text-base text-quaternary">{t("submit.successDescription")}</p>
                                <div className="pt-2">
                                    <Button href="/" size="lg" color="primary">
                                        {t("submit.backHome")}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <Input
                                    label={t("fields.fullName.label")}
                                    placeholder={t("fields.fullName.placeholder")}
                                    value={form.fullName}
                                    onChange={(value) => {
                                        setForm((prev) => ({ ...prev, fullName: value }));
                                        if (errors.fullName) setErrors((prev) => ({ ...prev, fullName: undefined }));
                                    }}
                                    isRequired
                                    isInvalid={Boolean(errors.fullName)}
                                    hint={errors.fullName || t("fields.fullName.hint")}
                                    size="md"
                                />
                                <Input
                                    type="email"
                                    label={t("fields.workEmail.label")}
                                    placeholder={t("fields.workEmail.placeholder")}
                                    value={form.workEmail}
                                    onChange={(value) => {
                                        setForm((prev) => ({ ...prev, workEmail: value }));
                                        if (errors.workEmail) setErrors((prev) => ({ ...prev, workEmail: undefined }));
                                    }}
                                    isRequired
                                    isInvalid={Boolean(errors.workEmail)}
                                    hint={errors.workEmail || t("fields.workEmail.hint")}
                                    size="md"
                                />
                                <Input
                                    label={t("fields.company.label")}
                                    placeholder={t("fields.company.placeholder")}
                                    value={form.company}
                                    onChange={(value) => {
                                        setForm((prev) => ({ ...prev, company: value }));
                                        if (errors.company) setErrors((prev) => ({ ...prev, company: undefined }));
                                    }}
                                    isRequired
                                    isInvalid={Boolean(errors.company)}
                                    hint={errors.company || t("fields.company.hint")}
                                    size="md"
                                />
                                <Select
                                    label={t("fields.requestType.label")}
                                    selectedKey={form.requestType}
                                    onSelectionChange={(key) => {
                                        if (!key) return;
                                        setForm((prev) => ({ ...prev, requestType: String(key) as ContactInquiryRequestType }));
                                    }}
                                    items={requestTypeItems}
                                    size="md"
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <Select
                                    label={t("fields.relationshipType.label")}
                                    selectedKey={form.relationshipType}
                                    placeholder={t("fields.relationshipType.placeholder")}
                                    onSelectionChange={(key) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            relationshipType: key ? String(key) : undefined,
                                        }));
                                    }}
                                    items={relationshipTypeItems}
                                    size="md"
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <TextArea
                                    label={t("fields.message.label")}
                                    placeholder={t("fields.message.placeholder")}
                                    value={form.message}
                                    onChange={(value) => {
                                        setForm((prev) => ({ ...prev, message: value }));
                                        if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
                                    }}
                                    isRequired
                                    isInvalid={Boolean(errors.message)}
                                    hint={errors.message || t("fields.message.hint")}
                                    rows={6}
                                />
                                <Checkbox
                                    isSelected={form.privacyConsent}
                                    onChange={(isSelected) => {
                                        setForm((prev) => ({ ...prev, privacyConsent: isSelected }));
                                        if (errors.privacyConsent) setErrors((prev) => ({ ...prev, privacyConsent: undefined }));
                                    }}
                                    label={t("fields.privacyConsent.label")}
                                    hint={errors.privacyConsent || t("fields.privacyConsent.hint")}
                                />

                                <Button type="submit" size="lg" color="primary" isLoading={createInquiry.isPending} className="w-full">
                                    {t("submit.cta")}
                                </Button>
                            </form>
                        )}
                </section>
            </main>
        </div>
    );
};
